<template>
	<div class="tables-select">
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="active" @toggling-popup="v => $emit('toggling-popup', v)">
			<div class="tables-select-content" v-if="active">
				<ModalHeader :title="getString('select_tables')" :button-text="getString('confirm')" @click-button="confirmChanges"/>
				<div class="row-filter">
					<div class="info-container-wrapper">
						<div class="info-container" :class="{satisfied: seatsSatisfied}">
							<div class="icon-container" v-html="svgStore.get('iconPerson')"></div>
							<div class="value">{{ selectedSeats }} / {{ reservationPeople ?? '0' }}</div>
						</div>
					</div>
					<AppButton outline :text="getString('show_all_tables')" :class="{activeFilter: showAllTables}"
							   @click="showAllTables = !showAllTables"/>
				</div>
				<div class="room-container">
					<AppList :items="roomsTables">
						<template #room="{item}">
							<div class="tables-container">
								<div v-if="item.tables.length > 0" class="table" v-for="table in item.tables" :key="table.room_table_id"
									 :class="{
									selected: isTableSelected(table.room_table_id),
									[getTableState(table)]: true,
									'has-reservation-overlay': !isNotToday && tablesCurrentReservations.hasOwnProperty(table.room_table_id)
									}"
									 @click="selectTable(table, item)">
									<div class="table-name">
										<p>{{ table.table_name }}</p>
										<div class="table-people">
											<div class="table-icon" v-html="svgStore.get('iconUser')"></div>
											<div class="people-num">{{ table.seat_number ? table.seat_number : '1' }}</div>
										</div>
									</div>
								</div>
								<div v-else>
									<p class="no-tables">{{ getString('no_tables') }}</p>
								</div>
							</div>
						</template>
						<template #tables-select-slot="{item}">
							<div class="row header-row" @click="clickRoomDropdown(item)">
								<p class="chevron-p">{{ item.name }}</p>
								<div class="chevron-container">
									<AppChevronIcon :direction="item.active ? 'up' : 'down'"/>
								</div>
							</div>
						</template>
					</AppList>
				</div>
			</div>
		</AppBottomSheet>
	</div>
</template>

<script>
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import {useLanguageStore, useSvgStore} from "@/shared/stores";
import AppList from "@/shared/components/AppList.vue";
import {useExperiencesStore, useRoomsStore, useRoomsOrdersStore, useUserStore} from "@/console-new/stores";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppButton from "@/shared/components/AppButton.vue";
import API from "@/shared/helpers/API";
import {formatDateOnly} from "@/shared/helpers/dates";

export default {
	setup() {
		let languageStore = useLanguageStore();
		const roomsStore = useRoomsStore();
		const roomsOrdersStore = useRoomsOrdersStore();
		const userStore = useUserStore();
		const experiencesStore = useExperiencesStore();
		const svgStore = useSvgStore();
		return {getString: languageStore.getString, languageStore, roomsStore, roomsOrdersStore, userStore, experiencesStore, svgStore};
	},
	name: "ReservationTablesSelect",
	components: {AppChevronIcon, AppList, ModalHeader, AppBottomSheet, AppButton},
	mixins: [WindowWidth],
	emits: ["toggling-popup", "confirm-changes"],
	props: {
		active: {
			type: Boolean,
			required: true,
		},
		experienceId: {
			type: [String, Number],
			default: null
		},
		initialSelectedTables: {
			type: Object,
			default: {}
		},
		reservationPeople: {
			type: Number,
			default: 0
		},
		reservationDate: {
			type: Date,
			default: null
		},
		reservationTime: {
			type: Date,
			default: null
		},
		reservationDuration: {
			type: Number,
			default: 60
		},
	},
	data() {
		return {
			selectedTables: {},
			showAllTables: false,
			dayReservations: [],
		}
	},
	computed: {
		experience() {
			return this.experienceId ? this.experiencesStore.businessExperiences.find(experience => String(experience.experience_id) === String(this.experienceId)) : null;
		},
		roomsTables() {
			if (this.experience && !this.showAllTables) {
				let roomsTables = this.roomsStore.roomsTables;
				let validTablesMap = {};
				this.experience.tables.forEach(table => {
					if (!validTablesMap[table.room_table_id]) {
						validTablesMap[table.room_table_id] = true;
					}
				});
				return roomsTables.map(room => {
					let filteredTables = room.tables.filter(table => validTablesMap[table.room_table_id]);
					if (filteredTables.length === 0) {
						return null;
					}
					return {
						...room,
						tables: room.tables.filter(table => validTablesMap[table.room_table_id]),
						title: room.name,
						customSlot: "tables-select-slot",
						dropdownSlot: "room",
						active: false,
					};
				}).filter(room => room !== null);
			} else {
				return this.roomsStore.roomsTables.map(room => {
					return {
						...room,
						title: room.name,
						customSlot: "tables-select-slot",
						dropdownSlot: "room",
						active: false,
					};
				});
			}
		},
		bottomSheetWidth() {
			if (this.windowWidth < 550) {
				return "100%";
			} else if (this.windowWidth < 768) {
				return "550px";
			} else if (this.windowWidth < 1080) {
				return "720px";
			} else if (this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
		selectedSeats() {
			let total = 0;
			this.roomsStore.roomsTables.forEach(room => {
				room.tables.forEach(table => {
					if (this.isTableSelected(table.room_table_id)) {
						total += table.seat_number ? table.seat_number : 0;
					}
				});
			});
			return total;
		},
		seatsSatisfied() {
			return this.selectedSeats >= this.reservationPeople && this.reservationPeople > 0;
		},
		reservationDateTime() {
			if (this.reservationDate && this.reservationTime) {
				let combined = new Date(this.reservationDate);
				combined.setHours(this.reservationTime.getHours(), this.reservationTime.getMinutes(), 0, 0);
				return combined;
			}
			return null;
		},
		isNotToday() {
			if (!this.reservationDate) {
				return false;
			}
			let today = new Date();
			return today.getFullYear() !== this.reservationDate.getFullYear() ||
				today.getMonth() !== this.reservationDate.getMonth() ||
				today.getDate() !== this.reservationDate.getDate();
		},
		tablesCurrentReservations() {
			if (!this.reservationDateTime) {
				return {};
			}
			let result = {};
			let startCheck = new Date(this.reservationDateTime);
			let endCheck = new Date(this.reservationDateTime.getTime() + this.reservationDuration * 60 * 1000);
			
			// Use locally-fetched reservations for the target date so we don't disturb the
			// global reservationsSelectedDate / reservationsCurrDate (which drive the
			// underlying /tables/reservations calendar).
			this.dayReservations.forEach(reservation => {
				if (!['confirmed', 'arrived'].includes(reservation.status)) return;
				
				// Check if this reservation overlaps with our time window
				let resStart = reservation.fullDate;
				let resDuration = reservation.duration ? Number(reservation.duration) : this.reservationDuration;
				let resEnd = new Date(resStart.getTime() + resDuration * 60 * 1000);
				
				if (resStart < endCheck && resEnd > startCheck) {
					// Add all tables from this reservation
					reservation.tables.forEach(tableId => {
						tableId = Number(tableId);
						if (result.hasOwnProperty(tableId)) {
							let competingRes = result[tableId];
							// Keep the earlier reservation
							if (reservation.fullDate < competingRes.fullDate) {
								result[tableId] = reservation;
							}
						} else {
							result[tableId] = reservation;
						}
					});
				}
			});
			
			return result;
		},
	},
	methods: {
		confirmChanges() {
			this.$emit("confirm-changes", Object.keys(this.selectedTables).filter(tableId => this.selectedTables[tableId]));
			this.$emit("toggling-popup", false);
		},
		clickRoomDropdown(room) {
			this.roomsTables.forEach(r => {
				if (r.room_id === room.room_id) {
					room.active = !room.active;
				} else {
					r.active = false;
				}
			})
		},
		selectTable(table, room = null) {
			this.selectedTables[table.room_table_id] = !this.isTableSelected(table.room_table_id);
		},
		isTableSelected(tableId) {
			return this.selectedTables.hasOwnProperty(tableId) && this.selectedTables[tableId];
		},
		openARoom() {
			if (this.roomsTables.length > 0) {
				let activeRoom = this.roomsTables.find(room => room.active);
				if (!activeRoom) {
					this.roomsTables[0].active = true;
				}
			}
		},
		getTableState(table) {
			// For dates other than today, show only free or reserved states
			if (this.isNotToday) {
				return this.tablesCurrentReservations.hasOwnProperty(table.room_table_id) ? 'reserved' : 'free';
			}
			// For today, show actual table state
			if (table.openDate) {
				return table.prebillRequested ? 'closing' : 'occupied';
			}
			return 'free';
		},
		loadDayReservations() {
			// Fetch the target date's reservations into local state for occupancy display,
			// without touching the global reservationsSelectedDate / reservationsCurrDate
			// that drive the underlying /tables/reservations calendar.
			if (!this.active || !this.reservationDate) {
				this.dayReservations = [];
				return;
			}
			const date = formatDateOnly(this.reservationDate);
			const status = ["confirmed", "arrived"];
			API.init().getDayReservations(this.userStore.currBusinessId, date, status, null, null, null, response => {
				if (response && response.result === "OK") {
					this.dayReservations = this.roomsOrdersStore.normalizeReservations(response.reservations);
				} else {
					this.dayReservations = [];
				}
			});
		},
	},
	mounted() {
		Object.keys(this.initialSelectedTables).forEach(tableId => {
			this.selectedTables[tableId] = this.initialSelectedTables[tableId];
		});
		this.loadDayReservations();
	},
	watch: {
		roomsTables: {
			handler(newVal) {
				if (newVal.length > 0) {
					this.openARoom();
				}
			},
			immediate: true,
		},
		initialSelectedTables: {
			handler(newVal) {
				Object.keys(newVal).forEach(tableId => {
					this.selectedTables[tableId] = newVal[tableId];
				});
			},
			immediate: true,
		},
		active() {
			this.loadDayReservations();
		},
		reservationDate() {
			this.loadDayReservations();
		}
	}
}
</script>

<style scoped lang="scss">
.chevron-p {
	width: calc(100% - 50px);
}

.header-row {
	display: flex;
	align-items: center;
	user-select: none;
	justify-content: space-between;
	cursor: pointer;
	padding: 6px 0;
}

.row-filter {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 10px;
	margin-top: 10px;
	border-bottom: 1px solid var(--color-border-alpha);
	padding-bottom: 10px;
	width: 100%;

	.base-button {
		&.activeFilter {
			background-color: var(--color-active-button);

			.btn-text p {
				color: var(--color-active-button-text);
			}
		}
	}

	.info-container-wrapper {
		display: flex;
		gap: 4px;
		justify-content: flex-end;
		align-items: center;
		height: 32px;

		@media (max-width: 500px) {
			flex-direction: column;
			justify-content: center;
			padding: 4px 0;
		}
	}

	.info-container {
		display: flex;
		align-items: center;
		gap: 8px;
		background-color: $alpha_2;
		border-radius: 13px;
		padding: 6px 8px;
		min-width: 66px;
		box-shadow: inset 0 1px 2px $alpha;

		&.satisfied {
			background-color: var(--color-fill-positive);

			.icon-container {
				&:deep(svg) {
					.fill {
						fill: var(--color-txt);
					}

					.stroke {
						stroke: var(--color-txt);
					}
				}
			}

			.value {
				color: var(--color-txt);
			}
		}

		.icon-container {
			display: flex;
			align-items: center;
			justify-content: center;

			&:deep(svg) {
				width: 16px;
				height: 16px;

				.fill {
					fill: var(--color-txt);
				}

				.stroke {
					stroke: var(--color-txt);
				}
			}
		}

		.value {
			font-size: 16px;
		}
	}
}

.tables-container {
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-start;
	align-items: flex-start;
	gap: 10px;

	.table {
		width: calc((100% / 4) - 10px);
		border-radius: 13px;
		cursor: pointer;
		transition: box-shadow 0.2s ease-in-out, background-color 0.2s ease-in-out, border 0.2s ease-in-out;
		box-shadow: 0 2px 3px var(--color-box-shadow-3);
		font-size: 16px;
		display: flex;
		justify-content: space-between;
		align-items: center;
		border: 1px solid transparent;
		position: relative;
		--card-color: var(--color-bg);
		--accent-color: var(--color-primary);
		--gradient-start-color: var(--color-primary);
		background-color: var(--card-color);

		&:hover {
			box-shadow: 0 2px 3px rgba(0, 0, 0, 0);
		}

		.table-name {
			width: 100%;
			display: flex;
			justify-content: space-between;
			align-items: center;

			p {
				@include truncateString(1);
				padding: 8px 4px 8px 12px;
				z-index: 2;
			}

			.table-people {
				display: flex;
				align-items: center;
				gap: 4px;
				font-size: 14px;
				color: var(--color-text);
				background-color: var(--color-alpha-2);
				height: 30px;
				margin-right: 12px;
				border-radius: 7px;
				box-shadow: inset 0 2px 3px var(--color-box-shadow-3);
				padding: 0 8px;
				z-index: 2;

				.table-icon {
					width: 14px;
					height: 14px;

					&:deep(svg) {
						width: 14px;
						height: 14px;

						.fill {
							fill: var(--color-primary);
						}

						.stroke {
							stroke: var(--color-primary);
						}
					}
				}

				.people-num {
					font-size: 16px;
					color: var(--color-primary);
					font-weight: 500;
				}
			}
		}

		.table-course {
			border-radius: 13px;
			border: 1px solid $primary;
			padding: 2px 4px;
			min-width: 26px;
			text-align: center;
			font-size: 12px;
			color: $primary;
			cursor: pointer;
			transition: background-color 0.25s ease-in-out;
			display: flex;
			justify-content: center;
			align-items: center;
			user-select: none;
		}

		&.selected {
			background: var(--color-active-button);
			border: 1px solid var(--color-primary);
			box-shadow: 0 2px 3px rgba(7, 82, 163, 0);
		}

		&.free {
			--card-color: var(--color-table-free);
			--accent-color: var(--color-table-gradient-start-free);
			--gradient-start-color: var(--color-table-gradient-end-free);
		}

		&.occupied {
			--card-color: var(--color-table-occupied);
			--accent-color: var(--color-table-gradient-start-occupied);
			--gradient-start-color: var(--color-table-gradient-end-occupied);
		}

		&.closing {
			--card-color: var(--color-table-closing);
			--accent-color: var(--color-table-gradient-start-closing);
			--gradient-start-color: var(--color-table-gradient-end-closing);
		}

		&.reserved {
			--card-color: var(--color-table-reserved);
		}

		&.has-reservation-overlay {
			&:after {
				content: "";
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				background: linear-gradient(180deg, rgba(7, 82, 163, 0) 0%, var(--color-primary-alpha-4) 60%, var(--color-primary-alpha-8) 100%);
				border-radius: 13px;
				z-index: 1;
				pointer-events: none;
			}
		}


		@media (max-width: 1440px) {
			width: calc((100% / 3) - 10px);
		}

		@media (max-width: 768px) {
			width: calc((100% / 2) - 10px);
		}

		@media (max-width: 450px) {
			width: calc((100%) - 10px);
		}

	}
}
</style>